import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'

// Import side-effect: registers contracts with the responseContract registry.
import '../contracts/AiCredits_PacksResponse'
import '../contracts/AiCredits_PackMutationResponse'
import '../contracts/AiCredits_PackInput'
import '../contracts/AiCredits_PackActiveInput'
import '../contracts/AiCredits_PackMarketInput'
import '../contracts/AiCredits_ReserveFloorResponse'

import type { AiCredits_PackRow } from '../contracts/AiCredits_PackRow'
import type { AiCredits_PackInput } from '../contracts/AiCredits_PackInput'
import type { AiCredits_PackActiveInput } from '../contracts/AiCredits_PackActiveInput'
import type { AiCredits_PackMarketInput } from '../contracts/AiCredits_PackMarketInput'
import type { AiCredits_ReserveFloorResponseDados } from '../contracts/AiCredits_ReserveFloorResponse'

/**
 * PackMarket — one per-(currency, provider_code) price row for a pack.
 * No provider_price_ref: packs are one-off charges (no Stripe Price objects).
 */
export interface PackMarket {
  currency: string
  price_cents: number
  provider_code: string
  countries: string[]
}

/**
 * useCreditPacks — SOLE network owner for ai/credits/packs/* routes, plus one
 * narrow exception: fetchReserveFloorMicro() below reads
 * ai/credits/pricing/reserve-floor (owned by ai/pricing.php, not ai/packs.php).
 * The Pacotes tab's reserve-floor flag (M5b-1) needs exactly this one read and
 * there is no dedicated pricing-read composable in this milestone's scope —
 * duplicating the flat-cap pricing formula in TypeScript instead would be the
 * exact defect class that endpoint exists to avoid, so the read lives here,
 * read-only, rather than growing a second composable for one GET.
 *
 * Endpoints:
 *   GET  ai/credits/packs                    → list all packs (Full: includes inactive)
 *   POST ai/credits/packs                    → create pack
 *   POST ai/credits/packs/{id}               → update pack
 *   POST ai/credits/packs/{id}/active        → toggle active
 *   POST ai/credits/packs/{id}/prices        → upsert per-market price row
 *   GET  ai/credits/pricing/reserve-floor    → worst-case flat-cap reserve, micro-credits
 */

// Module-scope singleton state
const packs = ref<AiCredits_PackRow[]>([])
const reserveFloorMicro = ref<number | null>(null)

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

export function useCreditPacks() {
  /**
   * fetchPacks — GET ai/credits/packs.
   * Populates the module-scope packs ref.
   */
  async function fetchPacks(): Promise<void> {
    const raw = await httpService.get(
      sistemaUrl('ai/credits/packs'),
      { responseContract: 'AiCredits_PacksResponse' },
    )
    const resp = raw as { dados: { rows: AiCredits_PackRow[] } }
    packs.value = resp.dados?.rows ?? []
    log('useCreditPacks', 'fetchPacks', 'Packs carregados', { count: packs.value.length })
  }

  /**
   * createPack — POST ai/credits/packs.
   * Returns the id of the newly created pack.
   */
  async function createPack(input: AiCredits_PackInput): Promise<number> {
    const raw = await httpService.post(
      sistemaUrl('ai/credits/packs'),
      input,
      {
        inputContract: 'AiCredits_PackInput',
        responseContract: 'AiCredits_PackMutationResponse',
        // Recusa em HTTP 200: sem isto `resp.dados.id` estoura um TypeError
        // sem nome em vez do motivo que o backend escreveu.
        throwOnEnvelopeFailure: 'pack_create_failed',
      },
    )
    const resp = raw as { dados: { id: number; ok: boolean } }
    log('useCreditPacks', 'createPack', 'Pack criado', { id: resp.dados.id })
    return resp.dados.id
  }

  /**
   * updatePack — POST ai/credits/packs/{id}.
   */
  async function updatePack(id: number, input: AiCredits_PackInput): Promise<void> {
    await httpService.post(
      sistemaUrl(`ai/credits/packs/${id}`),
      input,
      {
        inputContract: 'AiCredits_PackInput',
        responseContract: 'AiCredits_PackMutationResponse',
        throwOnEnvelopeFailure: 'pack_update_failed',
      },
    )
    log('useCreditPacks', 'updatePack', 'Pack atualizado', { id })
  }

  /**
   * saveMarketForPack — POST ai/credits/packs/{id}/prices.
   * Upserts a per-(currency, provider_code) price row for the given pack.
   * No provider_price_ref: packs are one-off (not recurring subscriptions).
   */
  async function saveMarketForPack(packId: number, market: PackMarket): Promise<void> {
    const body: AiCredits_PackMarketInput = {
      currency: market.currency,
      price_cents: market.price_cents,
      provider_code: market.provider_code,
      countries: market.countries,
    }
    // AiCredits_PacksResponse, NÃO PackMutationResponse: este endpoint devolve
    // TODAS as linhas de preço do pack (packs.php::upsert_price ->
    // listWithPricesByPackId), não o par {id, ok} das mutações de pack.
    //
    // Declarar o contrato errado aqui não era um detalhe de tipagem: a
    // validação de resposta LANÇA, então o mercado era gravado no banco e a
    // tela dizia "Erro ao salvar mercado" — a pior combinação possível, porque
    // convida a repetir uma operação que já deu certo.
    await httpService.post(
      sistemaUrl(`ai/credits/packs/${packId}/prices`),
      body,
      {
        inputContract: 'AiCredits_PackMarketInput',
        responseContract: 'AiCredits_PacksResponse',
        throwOnEnvelopeFailure: 'pack_price_failed',
      },
    )
    log('useCreditPacks', 'saveMarketForPack', 'Mercado salvo', { packId, currency: market.currency, provider_code: market.provider_code })
  }

  /**
   * setActive — POST ai/credits/packs/{id}/active.
   */
  async function setActive(id: number, active: 0 | 1): Promise<void> {
    const body: AiCredits_PackActiveInput = { active }
    await httpService.post(
      sistemaUrl(`ai/credits/packs/${id}/active`),
      body,
      {
        inputContract: 'AiCredits_PackActiveInput',
        responseContract: 'AiCredits_PackMutationResponse',
        throwOnEnvelopeFailure: 'pack_active_failed',
      },
    )
    log('useCreditPacks', 'setActive', 'Pack active atualizado', { id, active })
  }

  /**
   * fetchReserveFloorMicro — GET ai/credits/pricing/reserve-floor.
   * Populates reserveFloorMicro (micro-credits) with the worst-case flat-cap
   * reserve one agent turn would hold — the same number the admission gate
   * (POST /ai/credits/reserve) prices, priced server-side. Lets the Pacotes
   * tab flag a pack whose credits fall below this floor without re-deriving
   * the pricing formula in TypeScript. See the composable docblock above.
   */
  async function fetchReserveFloorMicro(): Promise<void> {
    const raw = await httpService.get(
      sistemaUrl('ai/credits/pricing/reserve-floor'),
      { responseContract: 'AiCredits_ReserveFloorResponse' },
    )
    const resp = raw as { dados: AiCredits_ReserveFloorResponseDados }
    reserveFloorMicro.value = resp.dados?.reserve_floor_micro ?? null
    log('useCreditPacks', 'fetchReserveFloorMicro', 'Piso de reserva carregado', { reserveFloorMicro: reserveFloorMicro.value })
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    packs.value = []
    reserveFloorMicro.value = null
  }

  return {
    packs,
    reserveFloorMicro,
    fetchPacks,
    createPack,
    updatePack,
    saveMarketForPack,
    setActive,
    fetchReserveFloorMicro,
    _reset,
  }
}
